import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MaskedLine } from "./MaskedReveal";

const EASE = [0.76, 0, 0.24, 1];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.1]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-40%"]);

  return (
    <section
      id="top"
      ref={ref}
      className="relative h-[100svh] w-full overflow-hidden bg-[#050505]"
      data-testid="hero-section"
    >
      {/* Parallax background */}
      <motion.div
        style={{ y, scale }}
        className="absolute inset-0 w-full h-[130%] -top-[15%]"
      >
        <img
          src="https://customer-assets-wrfwihn1.emergentagent.net/job_elyon-aviation/artifacts/dncpttzu_515147-Cessna-408-SkyCourier-007-881831-original-1701193735-v2.webp"
          alt="Cessna 408 Sky Courier at sunset"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 hero-vignette" />
      </motion.div>

      {/* Content */}
      <motion.div
        style={{ y: textY, opacity }}
        className="relative z-10 h-full flex flex-col justify-between mx-auto max-w-[1920px] px-6 md:px-12 lg:px-16 pt-32 pb-12"
      >
        {/* Top overline */}
        <div className="flex items-start justify-between gap-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2, duration: 1, ease: EASE }}
            className="font-mono-label text-white/60"
          >
            <span className="text-[#FF5A00]">◉</span> Worldwide
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.4, duration: 1, ease: EASE }}
            className="hidden md:block font-mono-label text-white/60 text-right"
          >
            Cessna 408 SkyCourier
            <br />
            <span className="text-[#FF5A00]">Insurance-Approved Training</span>
          </motion.div>
        </div>

        {/* Kinetic headline */}
        <div className="flex-1 flex flex-col justify-end">
          <h1 className="font-brand text-white leading-[0.9] tracking-[-0.02em] text-[16vw] md:text-[13vw] select-none">
            <MaskedLine delay={0.65} animate={{ y: "0%" }}>El Elyon</MaskedLine>
            <MaskedLine delay={0.85} animate={{ y: "0%" }}>Air</MaskedLine>
          </h1>

          <div className="mt-10 md:mt-14 grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-12 items-end">
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.7, duration: 1.1, ease: EASE }}
              className="md:col-span-6 text-white/70 text-base md:text-lg max-w-xl font-body leading-relaxed"
            >
              <span className="font-serif-italic italic text-white/90 text-xl md:text-2xl block mb-3">
                Mission Focused. Safety Driven. Operationally Proven.
              </span>
              El Elyon Air develops mission-ready aviation programs and delivers
              insurance-approved in-aircraft training — from the C408 SkyCourier
              and King Air to government and special-mission operations
              worldwide.
            </motion.p>
            <div className="md:col-span-4 md:col-start-9 flex items-center gap-4">
              <motion.a
                href="#contact"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.9, duration: 0.9, ease: EASE }}
                className="btn-outline btn-solid"
                data-testid="hero-cta-primary"
              >
                Begin Training
                <span aria-hidden>→</span>
              </motion.a>
              <motion.a
                href="#services"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 2.05, duration: 0.9, ease: EASE }}
                className="btn-outline"
                data-testid="hero-cta-secondary"
              >
                Our Services
              </motion.a>
            </div>
          </div>
        </div>

        {/* Bottom row */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2.2, duration: 1 }}
          className="mt-10 flex items-end justify-between font-mono-label text-white/50 text-[10px]"
        >
          <div>
            N °41.878 W °87.629
            <span className="mx-3 text-white/30">/</span>
            FL 380
          </div>
          <div className="hidden md:flex items-center gap-3">
            <span className="w-8 h-[1px] bg-white/40" />
            Scroll — Vol. 01
          </div>
          <div className="text-right">
            <span className="text-white">01</span>
            <span className="text-white/30"> — 06</span>
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
};
